import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Info } from "lucide-react";
import { createCreditNote } from "../../lib/api";
import { TAX_REGIME_LABELS } from "../../lib/taxRegimes";

// v14v Sprint 2B — Creditnota vanuit een definitieve factuur (Flow D).
// - BTW-regime + vat_rate per regel worden 1-op-1 overgenomen (read-only).
// - Alle regels standaard geselecteerd. Uitvinken = gedeeltelijke creditnota.
// - Bedragen worden op de backend negatief geboekt via unit_price flip.
// - Layout-pariteit met ProFormaDialog / VrijeFactuurDialog (Design System v1).

const fmtEur = (n) =>
  new Intl.NumberFormat("nl-BE", { style: "currency", currency: "EUR" })
    .format(Number(n || 0));

export default function CreditnotaVanuitFactuurDialog({
  open, onOpenChange, sourceInvoice, onCreated,
}) {
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);

  // Reset bij openen: alles standaard geselecteerd.
  useEffect(() => {
    if (!open || !sourceInvoice) return;
    const all = new Set((sourceInvoice.line_items || []).map((ln) => ln.id));
    setSelectedIds(all);
  }, [open, sourceInvoice]);

  const lines = sourceInvoice?.line_items || [];
  const regimeKey = sourceInvoice?.tax_regime_key || "standard";
  const isReverseCharge = regimeKey === "reverse_charge";

  const toggle = (lineId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId); else next.add(lineId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === lines.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(lines.map((ln) => ln.id)));
    }
  };

  // Live voorbeeld: negatieve totalen op basis van geselecteerde regels.
  const preview = useMemo(() => {
    let sub = 0, vat = 0;
    for (const ln of lines) {
      if (!selectedIds.has(ln.id)) continue;
      if (ln.line_type === "option") continue;
      const raw = Number(ln.quantity || 0) * Number(ln.unit_price_excl || 0);
      const excl = ln.line_type === "discount" ? -Math.abs(raw) : raw;
      sub += -excl;
      vat += -excl * Number(ln.vat_rate || 0) / 100;
    }
    if (isReverseCharge) {
      return { sub: Math.round(sub * 100) / 100, vat: 0, incl: Math.round(sub * 100) / 100 };
    }
    return {
      sub: Math.round(sub * 100) / 100,
      vat: Math.round(vat * 100) / 100,
      incl: Math.round((sub + vat) * 100) / 100,
    };
  }, [lines, selectedIds, isReverseCharge]);

  const isPartial = selectedIds.size > 0 && selectedIds.size < lines.length;
  const canSubmit = selectedIds.size > 0 && !!sourceInvoice?.id;

  const onSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // Volledig → geen line_ids nodig; deelselectie → expliciet meesturen.
      const body = isPartial
        ? { line_ids: Array.from(selectedIds), notes: "" }
        : { notes: "" };
      const cn = await createCreditNote(sourceInvoice.id, body);
      toast.success("Creditnota concept aangemaakt.");
      onOpenChange(false);
      if (typeof onCreated === "function") onCreated(cn);
      navigate(`/facturen/${cn.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aanmaken creditnota mislukt.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl w-[calc(100vw-1rem)] sm:w-full p-0 max-h-[95dvh] sm:max-h-[90vh] overflow-hidden flex flex-col gap-0"
        data-testid="credit-note-dialog"
      >
        <div className="flex-shrink-0 border-b border-[#E5E2DC] bg-white px-4 sm:px-6 py-3 sm:py-4">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="md:hidden inline-flex items-center gap-1 text-sm text-[#5D7259] font-semibold hover:underline mb-2 -ml-1"
            data-testid="credit-note-back-mobile"
          >
            <ArrowLeft className="w-4 h-4" /> Factuur
          </button>
          <DialogTitle className="font-heading text-xl sm:text-2xl text-[#2C2C2A] pr-8">
            Creditnota maken
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm text-[#4F4F4D] mt-1">
            {sourceInvoice?.invoice_number ? (
              <>Voor factuur <span className="font-mono">{sourceInvoice.invoice_number}</span>. Kies welke regels je wil crediteren — alle bedragen worden negatief geboekt.</>
            ) : (
              <>Kies welke regels je wil crediteren — alle bedragen worden negatief geboekt.</>
            )}
          </DialogDescription>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-5">
          {/* Read-only regime */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-[#2C2C2A]">BTW-regime</Label>
            <div className="flex items-center gap-2 flex-wrap" data-testid="credit-note-regime-readonly">
              <Badge variant="outline" className="bg-[#FAF9F6] border-[#E5E2DC] text-[#2C2C2A]">
                {TAX_REGIME_LABELS[regimeKey] || "Standaard BTW"}
              </Badge>
              <span className="text-[11px] text-[#6E6E6C] italic inline-flex items-center gap-1">
                <Info className="w-3 h-3" /> Overgenomen van de originele factuur — niet aanpasbaar.
              </span>
            </div>
          </div>

          {/* Regels */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium text-[#2C2C2A]">
                Regels ({selectedIds.size} / {lines.length} geselecteerd)
              </Label>
              <button
                type="button"
                onClick={toggleAll}
                className="text-xs text-[#B07A4F] font-semibold hover:underline"
                data-testid="credit-note-toggle-all"
              >
                {selectedIds.size === lines.length ? "Deselecteer alles" : "Selecteer alles"}
              </button>
            </div>

            <div className="border border-[#E5E2DC] rounded-xl overflow-hidden">
              {lines.length === 0 && (
                <div className="p-4 text-sm text-[#A8A7A0] italic">Geen regels op deze factuur.</div>
              )}
              {lines.map((ln, i) => {
                const raw = Number(ln.quantity || 0) * Number(ln.unit_price_excl || 0);
                const excl = ln.line_type === "discount" ? -Math.abs(raw) : raw;
                const checked = selectedIds.has(ln.id);
                return (
                  <label
                    key={ln.id || i}
                    className={`flex items-start gap-3 px-3 sm:px-4 py-3 border-b last:border-b-0 border-[#E5E2DC] cursor-pointer transition ${
                      checked ? "bg-[#FDF7F2]" : "bg-white hover:bg-[#FAF9F6]"
                    }`}
                    data-testid={`credit-note-line-${ln.id}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggle(ln.id)}
                      className="mt-1 data-[state=checked]:bg-[#B07A4F] data-[state=checked]:border-[#B07A4F]"
                      data-testid={`credit-note-line-check-${ln.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#2C2C2A] break-words">
                        {ln.description || <span className="italic text-[#A8A7A0]">(geen omschrijving)</span>}
                      </div>
                      <div className="text-[11px] text-[#6E6E6C] mt-0.5 flex flex-wrap gap-x-2">
                        <span>{Number(ln.quantity || 0)} × {fmtEur(ln.unit_price_excl)}</span>
                        <span>BTW {Number(ln.vat_rate || 0)}%</span>
                        {ln.line_type === "discount" && <span className="italic">Korting</span>}
                        {ln.line_type === "option" && <span className="italic">Optie (niet in totaal)</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-[#4F4F4D]">Origineel</div>
                      <div className="text-sm text-[#2C2C2A] font-medium">{fmtEur(excl)}</div>
                      <div className="text-xs text-[#B84B45] font-semibold mt-1">
                        Credit: {fmtEur(-excl)}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Preview totalen (negatief) */}
          <div className="rounded-xl bg-[#FAF9F6] border border-[#E5E2DC] p-4" data-testid="credit-note-totals">
            <div className="grid grid-cols-2 gap-1 text-sm">
              <div className="text-[#4F4F4D]">Subtotaal excl.</div>
              <div className="text-right text-[#2C2C2A]">{fmtEur(preview.sub)}</div>
              <div className="text-[#4F4F4D]">{isReverseCharge ? "BTW (verlegd)" : "Totaal BTW"}</div>
              <div className="text-right text-[#2C2C2A]">{fmtEur(preview.vat)}</div>
              <div className="text-[#2C2C2A] font-medium pt-1 border-t border-[#E5E2DC]">Totaal incl.</div>
              <div className="text-right font-bold text-[#B84B45] pt-1 border-t border-[#E5E2DC]" data-testid="credit-note-total-incl">
                {fmtEur(preview.incl)}
              </div>
            </div>
            <p className="text-[11px] text-[#6E6E6C] mt-2 italic">
              Live voorbeeld — de backend herberekent bij bewaren en definitief maken.
            </p>
          </div>
        </div>

        <div className="flex-shrink-0 border-t border-[#E5E2DC] bg-white px-4 sm:px-6 py-3 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="h-11 rounded-xl"
            data-testid="credit-note-cancel"
          >
            Annuleer
          </Button>
          <Button
            onClick={onSubmit} disabled={!canSubmit || submitting}
            className="h-11 rounded-xl bg-[#2C2C2A] hover:bg-[#1E1E1D] text-white"
            data-testid="credit-note-submit"
          >
            {submitting ? "Bezig…" : (isPartial ? "Gedeeltelijke creditnota aanmaken" : "Creditnota aanmaken")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
